import { useState } from 'react';
import * as Icons from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { creditPlans, creditCosts, enterpriseFeatures } from '../mock';

export default function Pricing({ onChoose }) {
  const [annual, setAnnual] = useState(true);

  return (
    <section
      id="pricing"
      className="relative py-24 md:py-28 text-white"
      style={{
        background:
          'radial-gradient(1000px 600px at 50% 30%, rgba(255,0,127,0.08) 0%, transparent 60%),' +
          'linear-gradient(180deg, #030303 0%, #000 100%)',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.4em] text-[#FF007F] font-semibold uppercase mb-4 font-mono">
            <Icons.Coins size={12} />
            Credits &amp; Pricing
          </div>
          <h2 className="font-display text-4xl md:text-6xl font-black tracking-[-0.03em] text-white">
            Simple{' '}
            <span className="italic bg-clip-text text-transparent bg-gradient-to-r from-[#00F0FF] to-[#FF007F]">
              credit-based
            </span>{' '}
            pricing.
          </h2>
          <p className="mt-5 text-white/60 text-lg leading-relaxed">
            Every plan comes with monthly credits. Spend them on AI turns,
            deployments, and integrations. Top-up anytime.
          </p>
        </div>

        <Tabs defaultValue="individual" className="mt-10">
          <div className="flex justify-center">
            <TabsList className="glass rounded-full p-1">
              <TabsTrigger value="individual" className="rounded-full px-6 py-2 data-[state=active]:bg-white data-[state=active]:text-black text-white/70">
                Individual
              </TabsTrigger>
              <TabsTrigger value="enterprise" className="rounded-full px-6 py-2 data-[state=active]:bg-white data-[state=active]:text-black text-white/70">
                Enterprise
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="individual" className="mt-10">
            <div className="flex items-center justify-center gap-3 mb-10">
              <span className={`text-sm ${!annual ? 'text-white font-semibold' : 'text-white/50'}`}>Monthly</span>
              <Switch checked={annual} onCheckedChange={setAnnual} />
              <span className={`text-sm ${annual ? 'text-white font-semibold' : 'text-white/50'}`}>
                Annual <span className="text-[#00F0FF] text-xs font-bold">(Save up to 16%)</span>
              </span>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {creditPlans.map((p) => {
                const Icon = Icons[p.icon] || Icons.Sparkles;
                const price = annual ? p.priceAnnual : p.price;
                return (
                  <div
                    key={p.name}
                    className={`lift rounded-2xl p-6 relative ${
                      p.highlight
                        ? 'pro-border-glow bg-black/60'
                        : 'glass'
                    }`}
                  >
                    {p.highlight && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-[#00F0FF] to-[#FF007F] text-black text-[10px] font-black tracking-widest shadow-lg font-mono uppercase">
                        Most Popular
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="font-display text-xl font-bold text-white">{p.name}</span>
                      <Icon size={16} className="text-[#00F0FF]" />
                    </div>
                    <div className="text-xs text-white/50 mt-1 min-h-[32px]">{p.tag}</div>

                    <div className="mt-4 flex items-baseline gap-1">
                      <span className="font-display text-4xl font-black text-white">${price}</span>
                      <span className="text-white/50 text-sm">/ mo</span>
                    </div>
                    <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] text-xs font-medium font-mono">
                      <Icons.Coins size={12} /> {p.credits.toLocaleString()} credits / mo
                    </div>

                    <ul className="mt-5 space-y-2">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-[13px] text-white/80">
                          <Icons.Check size={14} className="text-[#00F0FF] mt-0.5 shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>

                    <Button
                      onClick={() => onChoose && onChoose(p.name)}
                      className={`mt-6 w-full h-11 rounded-xl text-sm font-bold ${
                        p.highlight
                          ? 'bg-gradient-to-r from-[#00F0FF] to-[#FF007F] text-black hover:brightness-110'
                          : 'bg-white text-black hover:bg-slate-100'
                      }`}
                    >
                      {p.cta}
                    </Button>
                  </div>
                );
              })}
            </div>

            {/* Credit costs guide */}
            <div className="mt-14 glass rounded-2xl p-6 md:p-8">
              <div className="flex items-center gap-2 text-white font-bold">
                <Icons.Info size={16} className="text-[#00F0FF]" />
                How credits work
              </div>
              <p className="text-sm text-white/60 mt-1">
                Credits are consumed as you use the platform. Unused monthly credits reset. Top-up credits never expire.
              </p>
              <div className="mt-5 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {creditCosts.map((c) => (
                  <div key={c.action} className="flex items-center justify-between bg-black/40 rounded-xl border border-white/8 px-4 py-3">
                    <span className="text-sm text-white/80">{c.action}</span>
                    <span className="text-sm font-bold text-[#00F0FF] flex items-center gap-1 font-mono">
                      <Icons.Coins size={12} /> {c.cost}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-5 text-xs text-white/45 flex items-center gap-2 font-mono uppercase tracking-widest">
                <Icons.Zap size={12} className="text-[#00F0FF]" />
                Top-up rate: <span className="font-bold text-white">$1 = 5 credits</span> &middot; Min top-up: $20 &middot; Never expire
              </div>
            </div>

            {/* Merchant / Stripe hook */}
            <div className="mt-6 glass rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 border-2 border-dashed border-[#00F0FF]/25">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#00F0FF] to-[#FF007F] text-black inline-flex items-center justify-center shrink-0">
                  <Icons.CreditCard size={22} />
                </div>
                <div>
                  <div className="font-display font-bold text-white text-lg">
                    Bring your own merchant account
                  </div>
                  <p className="text-sm text-white/60 mt-1 max-w-xl">
                    Available on Pro and Scale plans. Connect your Stripe account
                    to charge your own end-users directly &mdash; subscriptions,
                    one-time payments, or usage-based billing.
                  </p>
                </div>
              </div>
              <Button
                onClick={() => onChoose && onChoose('merchant')}
                className="h-11 rounded-xl bg-white text-black hover:bg-slate-100 font-bold shrink-0"
              >
                Set up Stripe <Icons.ArrowRight size={14} className="ml-1.5" />
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="enterprise" className="mt-10">
            <div className="rounded-3xl pro-border-glow p-10 md:p-14 grid md:grid-cols-2 gap-10 items-center relative overflow-hidden bg-black/70 text-white">
              <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#00F0FF]/15 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-[#FF007F]/15 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] text-[10px] font-bold tracking-widest uppercase font-mono">
                  <Icons.Building2 size={12} /> Enterprise
                </div>
                <h3 className="font-display text-3xl md:text-5xl font-black mt-4 tracking-[-0.03em]">
                  Powering{' '}
                  <span className="italic bg-clip-text text-transparent bg-gradient-to-r from-[#00F0FF] to-[#FF007F]">
                    Fortune 500
                  </span>{' '}
                  brands.
                </h3>
                <p className="mt-4 text-white/70 text-lg leading-relaxed">
                  Custom credit pools, private clusters, compliance
                  certifications and a dedicated success engineer. Priced per seat.
                </p>
                <Button
                  onClick={() => onChoose && onChoose('Enterprise')}
                  className="mt-6 h-11 rounded-xl bg-gradient-to-r from-[#00F0FF] to-[#FF007F] text-black hover:brightness-110 font-bold"
                >
                  Talk to sales <Icons.ArrowRight size={16} className="ml-1" />
                </Button>
              </div>
              <ul className="relative grid grid-cols-1 gap-3">
                {enterpriseFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-white/85">
                    <span className="h-6 w-6 rounded-full bg-[#00F0FF]/15 border border-[#00F0FF]/30 inline-flex items-center justify-center shrink-0">
                      <Icons.Check size={13} className="text-[#00F0FF]" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
